import { Alert } from 'antd';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  BankActions,
  DetailsAction,
  FavoredContactsActions,
  SettingsActions,
  TransactionsActions,
} from '@portal/actions';
import { decreaseLoading, increaseLoading } from '@portal/actions/loading';
import { transfer } from '@portal/config/app.json';
import { getRouteStackPath } from '@portal/config/routes';
import { EventType } from '@portal/enum/analytics';
import { Weekday } from '@portal/models/enumerators';
import { encrypt, signData } from '@portal/repositories/securitySdkConfig';
import { sendEvent } from '@portal/services/firebase';
import { translate } from '@portal/services/i18n';
import {
  SEND_TOTP_FOR_USER,
  SET_NEW_CONTACT,
  SET_SUCCESS_PIN_FOR_TOTP,
} from '~/actions/actionTypes';

import PixIcon from '@portal/assets/svg/pix-buttons/ic_transfer.svg?react';
import TedIcon from '@portal/assets/svg/transfer-icons/ic_ted-doc.svg?react';
import P2PIcon from '@portal/assets/svg/transfer-icons/ic_tef-p2p.svg?react';

import { totpTransfer } from '@portal/actions/totpActions';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import ConfirmationDetails from '@portal/components/ConfirmationDetails/ConfirmationDetails';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import NewContactModal from '@portal/components/NewContactModal/NewContactModal';
import ValueRequestedDetails from '@portal/components/ValueRequestedDetails/ValueRequestedDetails';
import useReduxState from '@portal/hooks/useReduxState';
import * as MessageService from '@portal/services/message';
import ScheduleAlertModal from './ScheduleAlertModal';

import MaintenanceModal from '@portal/components/MaintenanceModal/MaintenanceModal';
import RolesType from '@portal/enum/rolesType';
import { TransferLimitsFields, TransferType } from '@portal/enum/transfers';
import { TransferTabs } from '@portal/enum/transferTabs';
import { hasAccess } from '@portal/utils/hasAccess';
import { getMaxValueError, getMinValueError } from '@portal/utils/transfer';

import {
  CloseCircleOutlined,
  EditFilled,
  PlusCircleOutlined,
} from '@ant-design/icons';
import AdvancedContactList from '@portal/components/AdvancedContactList/AdvancedContactList';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import AdvancedSteps from '@portal/components/AdvancedSteps/AdvancedSteps';
import OverdraftConfirmationModal from '@portal/components/OverdraftConfirmationModal/OverdraftConfirmationModal';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import TransactionSuccessDetails from '@portal/components/TransactionSuccessDetails/TransactionSuccessDetails';
import { useNavigation } from '@portal/hooks/useNavigation';
import { useOverdraft } from '@portal/hooks/useOverdraft';
import { P2PInfo } from '@portal/models/bank';
import AccountApi from '@portal/repositories/account';
import { formatCurrency, formatMoney } from '@portal/services/masks';
import { formatToHourAndMinute } from '@portal/utils/format';

const Transfer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const {
    user: { roles },
    bank: { list: banks },
    loading: { load: loading },
    favoredContacts: { list: contacts, newContact },
    details: { transaction, error },
    transactions: { sendSmsForTransaction, successTransferWithTotp },
    settings: { settings, remoteConfig },
    theme: { currentTheme },
  } = useReduxState();

  const [amount, setAmount] = useState('0');
  const [rawAmount, setRawAmount] = useState(0);
  const [tabKey, setTabKey] = useState<TransferTabs>(TransferTabs.TYPE);
  const [favored, setFavored] = useState<models.Contact | null>(null);
  const [transferType, setTransferType] = useState<TransferType | undefined>();
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [isPinModalVisible, setIsPinModalVisible] = useState(false);
  const [isNewContactModalVisible, setIsNewContactModalVisible] =
    useState(false);
  const [isTokenModalVisible, setIsTokenModalVisible] = useState(false);
  const [openMaintenanceModal, setOpenMaintenanceModal] = useState(false);
  const [showOverdraftModalState, setShowOverdraftModalState] = useState(false);
  const [overdraftOptionRequired, setOverdraftOptionRequired] = useState(false);
  const [overdraftOptionChecked, setOverdraftOptionChecked] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<string | null>(null);
  const [transactionScheduleAt, setTransactionScheduleAt] = useState(
    DateTime.now()
  );

  const {
    overdraftAmount,
    checkOverdraftUsage,
    confirmOverdraftUsage,
    cancelOverdraftUsage,
  } = useOverdraft();

  useEffect(() => {
    if (newContact) {
      handleClickContact(newContact);
      dispatch({
        type: SET_NEW_CONTACT,
        payload: null,
      });
    }
  }, [dispatch, newContact]);

  useEffect(() => {
    dispatch(BankActions.getBanks());
    dispatch(FavoredContactsActions.getFavoredContact());
  }, [dispatch]);

  useEffect(() => {
    dispatch(DetailsAction.unsetDetails());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      MessageService.error(`CONTACTS.${error.message}`, {
        originalError: error,
      });

      dispatch(DetailsAction.setDetailsError(null));
    }
  }, [dispatch, error]);

  useEffect(() => {
    if (!settings) {
      dispatch(SettingsActions.getSettings());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!remoteConfig) {
      dispatch(SettingsActions.getRemoteConfig());
    }
  }, [dispatch, remoteConfig]);

  const handleValueRequested = () => {
    if (!favored) {
      return MessageService.error('CONTACTS.SELECT_A_CONTACT');
    }

    const isTed = transferType === TransferType.TED;
    const limits =
      settings?.limits?.[
        isTed ? TransferLimitsFields.TED : TransferLimitsFields.P2P
      ];

    if (amount === '0' || !rawAmount || rawAmount < (limits?.min || 0)) {
      if (!limits?.min) {
        return MessageService.error('INVALID_AMOUNT');
      }

      return MessageService.error(getMinValueError(isTed).key, {
        interpolation: {
          interpolationField: getMinValueError(isTed).interpolation,
          interpolationValue: formatMoney(limits?.min),
        },
      });
    }

    if (limits?.max && rawAmount > limits.max) {
      return MessageService.error(getMaxValueError(isTed).key, {
        interpolation: {
          interpolationField: getMaxValueError(isTed).interpolation,
          interpolationValue: formatMoney(limits.max),
        },
      });
    }

    let isInvalidHour = false;

    if (isTed) {
      const currentDateFormatted = DateTime.now().toISODate();
      const nowUtc = DateTime.now().toUTC();
      const now = DateTime.now();

      if (settings?.tedMaxHourLimit) {
        const formattedMaxHour = DateTime.utc().set({
          hour: settings?.tedMaxHourLimit,
          minute: settings?.tedMaxMinuteLimit ?? 0,
        });

        isInvalidHour = isInvalidHour || nowUtc > formattedMaxHour;
      }

      if (settings?.tedMinHourLimit) {
        const formattedMinHour = DateTime.utc().set({
          hour: settings?.tedMinHourLimit,
          minute: settings?.tedMinMinuteLimit ?? 0,
        });

        isInvalidHour = isInvalidHour || nowUtc < formattedMinHour;
      }

      if (transfer.tedBusinessTimeOnly) {
        const isHoliday = settings
          ? settings.holidays.includes(currentDateFormatted)
          : false;

        const isWeekend = [Weekday.SATURDAY, Weekday.SUNDAY].includes(
          Number(now.weekday)
        );

        isInvalidHour = isInvalidHour || isWeekend || isHoliday;
      }

      if (isInvalidHour) {
        if (settings?.tedMaxHourLimit && settings.tedMinHourLimit) {
          setIsAlertVisible(true);
        }
      }
    }

    if (!isInvalidHour) {
      setTabKey(TransferTabs.CONFIRMATION);
    }
  };

  const handleAdvanceToPin = async () => {
    const isTed = transferType === TransferType.TED;
    const totalAmount =
      isTed && settings?.tariffs?.ted
        ? rawAmount + settings.tariffs.ted
        : rawAmount;
    const willUseOverdraft = checkOverdraftUsage(totalAmount);
    if (willUseOverdraft) {
      dispatch(increaseLoading());
      setShowOverdraftModalState(true);
      setOverdraftOptionChecked(false);
      try {
        const hasHash = await AccountApi.getOverdraftHash();
        setOverdraftOptionRequired(!hasHash);
      } catch (e) {
        setOverdraftOptionRequired(true);
      } finally {
        dispatch(decreaseLoading());
      }
      return;
    }
    setIsPinModalVisible(true);
  };

  const handlePinConfirmation = async (pin: string) => {
    if (!favored?.id) {
      return;
    }
    const timestamp = new Date().getTime().toString();

    const amount = rawAmount * 100;
    const transactionHash = signData(amount, favored.id, timestamp);
    const encryptedPIN = encrypt(pin);
    const isScheduled = transactionScheduleAt.diffNow('days').days > 0;

    if (transferType === 'p2p' && isScheduled) {
      const p2pInfo: P2PInfo = {
        payeeId: favored.id,
      };

      dispatch(
        TransactionsActions.p2pScheduledTransfer(
          p2pInfo,
          rawAmount,
          {
            pin: encryptedPIN,
            transactionHash,
            timestamp,
          },
          transactionScheduleAt.toISODate(),
          favored.id
        )
      );
      setIsPinModalVisible(false);
      return;
    }

    dispatch(
      TransactionsActions.contactTransfer(favored.id, rawAmount, {
        pin: encryptedPIN,
        timestamp,
        transactionHash,
      })
    );

    setIsPinModalVisible(false);
  };

  const handleOverdraftConfirmation = async () => {
    if (overdraftOptionRequired) {
      if (!overdraftOptionChecked) return;
      dispatch(increaseLoading());
      try {
        await AccountApi.postOverdraftSignature(
          translate('MODALS.OVERDRAFT_CONFIRMATION.OPTIN_CHECKBOX')
        );
        setOverdraftOptionRequired(false);
      } catch (e) {
        dispatch(decreaseLoading());
        return;
      }
      dispatch(decreaseLoading());
    }
    confirmOverdraftUsage();
    setShowOverdraftModalState(false);
    setIsPinModalVisible(true);
  };

  const handleOverdraftCancel = () => {
    cancelOverdraftUsage();
    setShowOverdraftModalState(false);
    setOverdraftOptionChecked(false);
    setOverdraftOptionRequired(false);
  };

  const handleNewContactModal = () => {
    setIsNewContactModalVisible(true);
  };

  const handleClickContact = (contact: models.Contact) => {
    setTabKey(TransferTabs.VALUE);
    setFavored(contact);
  };

  const handleSubmit = (contact: models.Contact) => {
    dispatch(FavoredContactsActions.createFavoredContact(contact));
    setIsNewContactModalVisible(false);
  };

  const actions = [
    {
      icon: <TedIcon />,
      title: translate('PAGES.HOME.TRANSFER.ACTION.TRANSFER.TITLE'),
      text: translate('PAGES.HOME.TRANSFER.ACTION.TRANSFER.TEXT'),
      type: 'ted',
      onClick: () => {
        if (remoteConfig?.disableTed || remoteConfig?.disableCashOut) {
          setOpenMaintenanceModal(true);

          return;
        }

        dispatch(DetailsAction.unsetDetails());
        setTransferType(TransferType.TED);
        setTabKey(TransferTabs.RECEIVER);
        sendEvent(EventType.BOTTOM_MODAL_TRANSFER_TED);
      },
      hidden: !transfer.ted,
    },
    {
      icon: <P2PIcon />,
      title: translate('PAGES.HOME.TRANSFER.ACTION.TRANSFER_2.TITLE'),
      text: translate('PAGES.HOME.TRANSFER.ACTION.TRANSFER.TEXT_2'),
      type: 'p2p',
      onClick: () => {
        if (remoteConfig?.disableCashOut) {
          setOpenMaintenanceModal(true);

          return;
        }

        dispatch(DetailsAction.unsetDetails());
        setTransferType(TransferType.P2P);
        setTabKey(TransferTabs.RECEIVER);
        sendEvent(EventType.BOTTOM_MODAL_TRANSFER_P2P);
      },
      hidden: !transfer.p2p,
    },
    {
      icon: <PixIcon />,
      title: translate('PAGES.HOME.TRANSFER.ACTION.TRANSFER_3.TITLE'),
      text: translate('PAGES.HOME.TRANSFER.ACTION.TRANSFER_3.TEXT'),
      type: 'pix',
      onClick: () => {
        if (remoteConfig?.disableCashOut) {
          setOpenMaintenanceModal(true);

          return;
        }
        sendEvent(EventType.BOTTOM_MODAL_TRANSFER_PIX);
        navigate(getRouteStackPath('PIX', 'TRANSFER'));
      },
      hidden: !transfer.pix && hasAccess(roles, RolesType.TRANSACTION_CREATE),
    },
  ];

  useEffect(() => {
    dispatch({
      type: SEND_TOTP_FOR_USER,
      payload: false,
    });
  }, [dispatch]);

  useEffect(() => {
    if (sendSmsForTransaction) {
      setIsTokenModalVisible(true);
      dispatch({
        type: SEND_TOTP_FOR_USER,
        payload: false,
      });
    }
  }, [sendSmsForTransaction, dispatch]);

  useEffect(() => {
    if (successTransferWithTotp) {
      dispatch({
        type: SET_SUCCESS_PIN_FOR_TOTP,
        payload: false,
      });

      if (transaction?.id) {
        navigate('/inicio/detalhes/' + transaction.id, {});
      } else {
        setTabKey(TransferTabs.DETAILS);
        setIsPinModalVisible(false);
      }
    }
  }, [successTransferWithTotp, transaction?.id, dispatch, history]);

  const handleSmsConfirmation = (pin: string) => {
    dispatch(
      totpTransfer({ token: pin, transactionId: transaction?.id ?? '' })
    );
  };

  const AdvancedButtonContainer = (
    <div className="transfers__button">
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
        variant="outlined"
        onClick={() => {
          setTabKey(TransferTabs.RECEIVER);
          setAmount('0');
          // Limpa o estado dos detalhes ao voltar
          dispatch(DetailsAction.unsetDetails());
        }}
      />
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.ADVANCE')}
        onClick={handleValueRequested}
      />
    </div>
  );

  const ConfirmPayment = (
    <div className="transfers__button">
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
        variant="outlined"
        onClick={() => setTabKey(TransferTabs.VALUE)}
      />
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.CONFIRM')}
        onClick={handleAdvanceToPin}
      />
    </div>
  );

  const AdvanceForWho = (
    <div className="transfers__button">
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
        variant="outlined"
        onClick={() => {
          setTransferType(undefined);
          setTabKey(TransferTabs.TYPE);
          dispatch(DetailsAction.unsetDetails());
        }}
      />
    </div>
  );

  const stepOptions = [
    {
      value: TransferTabs.TYPE,
      label: translate('PAGES.HOME.TRANSFER.TABS.TRANSFER_TYPE'),
      children: (
        <div className="transfers__grid">
          {actions
            .filter((a) => !a.hidden)
            .map((action) => (
              <button
                key={action.title}
                className="transfers__grid__button transparent-button"
                onClick={action.onClick}
                onMouseEnter={() => setHoveredIndex(action.type)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  border: `2px solid ${currentTheme.borderColor}`,
                  background:
                    hoveredIndex === action.type
                      ? currentTheme.background
                      : 'transparent',
                }}
              >
                <div
                  className="transfers__grid__button__icon"
                  style={{ background: currentTheme.background }}
                >
                  {action.icon}
                </div>
                <div>
                  <ThemedText className="transfers__grid__button__title">
                    {action.title}
                  </ThemedText>
                  <ThemedText
                    variant="body"
                    className="transfers__grid__button__description"
                  >
                    {action.text}
                  </ThemedText>
                </div>
              </button>
            ))}
        </div>
      ),
    },
    {
      value: TransferTabs.RECEIVER,
      label: translate('PAGES.HOME.TRANSFER.TABS.FOR_WHO'),
      children: (
        <>
          {transferType && !loading ? (
            <AdvancedContactList
              testId="advanced-contact-list-transfer"
              banks={banks}
              type={transferType}
              contacts={contacts}
              onClickNewContact={handleNewContactModal}
              onClickContact={handleClickContact}
            />
          ) : null}
        </>
      ),
    },
    {
      value: TransferTabs.VALUE,
      label: translate('PAGES.HOME.TRANSFER.TABS.REQUEST_VALUE'),
      children: (
        <div className="transfers__card">
          <div className="transfers__card__header">
            <ThemedText
              variant="body"
              className="value-requested__how-much-text"
            >
              {translate('PAGES.HOME.PIX.TRANSFER.HOW_MUCH')} {favored?.alias}
            </ThemedText>
            {transfer.changeFavoredLink ? (
              <button
                className="transfers__card__change-favored transparent-button"
                onClick={() => setTabKey(TransferTabs.RECEIVER)}
              >
                {translate('PAGES.HOME.TRANSFER.CHANGE_FAVORED')}
                <EditFilled />
              </button>
            ) : null}
          </div>
          <ValueRequestedDetails
            value={amount}
            onChange={(value, rawValue) => {
              setAmount(value);
              setRawAmount(rawValue);
            }}
          />
          <div className="transfers__quick-amounts">
            {[50, 100, 250, 500, 1000].map((rawValue) => (
              <AdvancedButton
                key={rawValue}
                type="button"
                onClick={() => {
                  setRawAmount((prev) => {
                    const newValue = (prev || 0) + rawValue;
                    setAmount(formatCurrency(newValue));
                    return newValue;
                  });
                }}
                variant="outlined"
                size="small"
                text={formatCurrency(rawValue)}
                startIcon={<PlusCircleOutlined className="transfers__icon" />}
              />
            ))}
            <AdvancedButton
              onClick={() => {
                setAmount('');
                setRawAmount(0);
              }}
              variant="outlined"
              size="small"
              text={translate('PAGES.HOME.TRANSFER.CLEAR')}
              startIcon={<CloseCircleOutlined className="transfers__icon" />}
            />
          </div>
        </div>
      ),
    },
    {
      value: TransferTabs.CONFIRMATION,
      label: translate('PAGES.HOME.TRANSFER.TABS.CONFIRMATION'),
      children: (
        <div>
          {favored && transferType ? (
            <ConfirmationDetails
              methodType="contact"
              destiny={favored}
              transferType={transferType}
              value={amount}
              setTabKey={setTabKey}
              {...(transferType === 'p2p' && {
                setTransactionScheduleAt,
                transactionScheduleAt,
              })}
            />
          ) : null}
        </div>
      ),
    },
    {
      value: TransferTabs.DETAILS,
      label: translate('PAGES.HOME.TRANSFER.TABS.DETAILS'),
      children: transaction && (
        <TransactionSuccessDetails
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          transaction={transaction as any}
        />
      ),
    },
  ];

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: false,
            title: translate('COMPONENTS.BREADCRUMB.TRANSFER'),
            url: getRouteStackPath('TRANSFERS', 'TRANSFERS'),
          },
        ]}
      />
      <div className="transfers">
        <AdvancedPanelContentHeader
          testId="advanced-panel-content-header-transfer"
          title={translate('PAGES.HOME.TRANSFER.PAGE_HEADER.TITLE')}
          description={
            transfer.description
              ? translate('PAGES.HOME.TRANSFER.PAGE_HEADER.DESCRIPTION')
              : ''
          }
          goBackAction={() => navigate(getRouteStackPath('HOME', 'HOME_PAGE'))}
          buttonTitle={translate('APPLICATION.BACK_TO_INIT')}
        />
        {transfer.timeLimitAlert &&
        settings &&
        settings.tedMinHourLimit &&
        settings.tedMaxHourLimit ? (
          <div className="transfers__container__alert">
            <Alert
              description={`${translate('PAGES.HOME.TRANSFER.MODAL.TEXT')}
                ${formatToHourAndMinute(
                  settings.tedMinHourLimit - 3,
                  settings.tedMinMinuteLimit ?? 0
                )}
                
                    ${translate('PAGES.HOME.TRANSFER.MODAL.AT')}
                    ${formatToHourAndMinute(
                      settings.tedMaxHourLimit - 3,
                      settings.tedMaxMinuteLimit ?? 0
                    )}
                    ${translate('PAGES.HOME.TRANSFER.MODAL.DAYS')}
                    ${translate('PAGES.HOME.TRANSFER.MODAL.TEXT_2')}
                    `}
              type="info"
              showIcon
            />
          </div>
        ) : null}
        <AdvancedSteps
          testId="advanced-steps-pix-transfer"
          steps={stepOptions}
          currentStep={tabKey}
          onStepChange={(step) => setTabKey(step as TransferTabs)}
        />
        {tabKey === TransferTabs.RECEIVER && AdvanceForWho}
        {tabKey === TransferTabs.VALUE && AdvancedButtonContainer}
        {tabKey === TransferTabs.CONFIRMATION &&
          !isAlertVisible &&
          ConfirmPayment}
      </div>
      <ConfirmationModal
        visible={isPinModalVisible}
        setVisible={setIsPinModalVisible}
        handleConfirmation={handlePinConfirmation}
        text={translate('PAGES.HOME.TRANSFER.PIN_CONFIRMATION_TEXT')}
        title={translate('PAGES.HOME.TRANSFER.PIN_CONFIRMATION_TITLE')}
      />
      <ConfirmationModal
        mode="number"
        visible={isTokenModalVisible}
        setVisible={setIsTokenModalVisible}
        handleConfirmation={handleSmsConfirmation}
        text={translate('PAGES.HOME.TRANSFER.SMS_CONFIRMATION_TEXT')}
        title={translate('PAGES.HOME.TRANSFER.SMS_CONFIRMATION_TITLE')}
        isSms
      />
      {isNewContactModalVisible ? (
        <NewContactModal
          type={transferType ?? TransferType.P2P}
          visible={isNewContactModalVisible}
          closeModal={() => setIsNewContactModalVisible(false)}
          onSubmit={handleSubmit}
        />
      ) : null}
      <ScheduleAlertModal
        isAlertVisible={isAlertVisible}
        setIsAlertVisible={setIsAlertVisible}
        setTabKey={setTabKey}
      />
      <MaintenanceModal
        setVisible={setOpenMaintenanceModal}
        visible={openMaintenanceModal}
      />
      <OverdraftConfirmationModal
        visible={showOverdraftModalState}
        overdraftAmount={overdraftAmount}
        onConfirm={handleOverdraftConfirmation}
        onCancel={handleOverdraftCancel}
        optinRequired={overdraftOptionRequired}
        optinChecked={overdraftOptionChecked}
        onOptinChange={setOverdraftOptionChecked}
      />
    </>
  );
};

export default Transfer;
